/**
 * Fase 2A · P0-3 · OnboardingWizard (shared)
 * Wrapper reutilizable para wizards de onboarding por rol.
 * Persistencia: localStorage por rol (Fase 2A · server-side queda para Fase 2B).
 * Props:
 *  - roleKey: string único (ej: "empleado", "colaborador", "gestor", "cliente")
 *  - title, subtitle, steps: [{ id, label, render: (props) => JSX }]
 *  - onComplete: () => void
 *  - homeRoute: string · a dónde ir cuando skip o al finalizar
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import {
  CheckCircle, ArrowRight, ArrowLeft, X as XIcon, Sparkle,
} from "@phosphor-icons/react";

function storageKey(roleKey) {
  return `ag:onboarding:${roleKey}`;
}

function loadState(roleKey, initial = {}) {
  try {
    const raw = localStorage.getItem(storageKey(roleKey));
    if (!raw) return { stepIdx: 0, data: initial, completedAt: null };
    const parsed = JSON.parse(raw);
    return {
      stepIdx: Number(parsed.stepIdx || 0),
      data: { ...initial, ...(parsed.data || {}) },
      completedAt: parsed.completedAt || null,
    };
  } catch {
    return { stepIdx: 0, data: initial, completedAt: null };
  }
}

function saveState(roleKey, state) {
  try {
    localStorage.setItem(storageKey(roleKey), JSON.stringify(state));
  } catch { /* ignore */ }
}

export default function OnboardingWizard({
  roleKey,
  title,
  eyebrow = "Bienvenido",
  subtitle,
  steps = [],
  initialData = {},
  initialStepIdx,
  homeRoute = "/",
  homeCta = "Ir al inicio",
  onComplete,
  onSkip,
}) {
  const navigate = useNavigate();
  const [{ stepIdx, data, completedAt }, setState] = useState(() => {
    const loaded = loadState(roleKey, initialData);
    if (typeof initialStepIdx === "number" && initialStepIdx >= 0) {
      return { ...loaded, stepIdx: initialStepIdx };
    }
    return loaded;
  });

  useEffect(() => {
    saveState(roleKey, { stepIdx, data, completedAt });
  }, [roleKey, stepIdx, data, completedAt]);

  const total = steps.length;
  const pct = total === 0 ? 100 : Math.round(((stepIdx + (completedAt ? 1 : 0)) / total) * 100);

  const current = steps[Math.min(stepIdx, total - 1)];
  const isLast = stepIdx >= total - 1;
  const isDone = !!completedAt;

  const updateData = (patch) => setState((s) => ({ ...s, data: { ...s.data, ...patch } }));

  const goNext = () => {
    if (isLast) {
      const now = new Date().toISOString();
      setState((s) => ({ ...s, completedAt: now }));
      if (typeof onComplete === "function") onComplete(data);
    } else {
      setState((s) => ({ ...s, stepIdx: Math.min(total - 1, s.stepIdx + 1) }));
    }
  };
  const goPrev = () => setState((s) => ({ ...s, stepIdx: Math.max(0, s.stepIdx - 1) }));
  const skipAll = () => {
    if (typeof onSkip === "function") onSkip(data);
    navigate(homeRoute);
  };

  if (isDone) {
    return (
      <CompletionCard
        eyebrow={eyebrow}
        homeRoute={homeRoute}
        homeCta={homeCta}
        onNavigate={() => navigate(homeRoute)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-[var(--ag-midnight)] text-[var(--ag-text)] flex flex-col ag-radial">
      {/* Top bar */}
      <div className="border-b border-[var(--ag-border)] px-4 md:px-8 py-3 flex items-center justify-between gap-3">
        <Link to="/welcome" className="text-[10px] uppercase tracking-[0.2em] text-[var(--ag-gold)] font-semibold hover:opacity-80">
          AutoGlow
        </Link>
        <button
          type="button"
          onClick={skipAll}
          className="text-xs text-[var(--ag-text-dim)] hover:text-white inline-flex items-center gap-1"
          data-testid={`onboarding-${roleKey}-skip-all`}
        >
          Saltar por ahora <XIcon size={12} />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-start justify-center p-4 md:p-8">
        <div className="w-full max-w-2xl space-y-6" data-testid={`onboarding-${roleKey}-wizard`}>
          <header>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ag-gold)] font-semibold">
              {eyebrow}
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-black tracking-tighter mt-1">
              {title}
            </h1>
            {subtitle && <p className="text-sm text-[var(--ag-text-dim)] mt-2">{subtitle}</p>}
          </header>

          {/* Progress */}
          <div className="space-y-2" data-testid={`onboarding-${roleKey}-progress`}>
            <div className="flex items-center justify-between text-xs">
              <span className="text-[var(--ag-text-dim)] tabular-nums">
                Paso {stepIdx + 1} de {total} · {current?.label}
              </span>
              <span className="font-bold text-[var(--ag-gold)] tabular-nums">{pct}%</span>
            </div>
            <div className="h-2 w-full bg-[var(--ag-midnight-soft)] border border-[var(--ag-border)] rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[var(--ag-gold)] to-[var(--ag-gold-strong)] transition-all duration-500"
                style={{ width: `${pct}%` }}
                data-testid={`onboarding-${roleKey}-progress-bar`}
              />
            </div>
          </div>

          {/* Current step */}
          <section className="ag-card p-5 md:p-6" data-testid={`onboarding-${roleKey}-step-${current?.id}`}>
            {current && current.render({ data, updateData })}
          </section>

          {/* Nav */}
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <Button
              type="button"
              variant="outline"
              onClick={goPrev}
              disabled={stepIdx === 0}
              className="bg-transparent border-[var(--ag-border)] text-[var(--ag-text-dim)] hover:text-white"
              data-testid={`onboarding-${roleKey}-prev`}
            >
              <ArrowLeft size={14} className="mr-1.5" /> Anterior
            </Button>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={goNext}
                className="text-xs text-[var(--ag-text-dim)] hover:text-white underline-offset-4 hover:underline"
                data-testid={`onboarding-${roleKey}-skip-step`}
              >
                Saltar este paso
              </button>
              <Button
                type="button"
                onClick={goNext}
                className="ag-btn-gold"
                data-testid={`onboarding-${roleKey}-next`}
              >
                {isLast ? "Finalizar" : "Continuar"} <ArrowRight size={14} className="ml-1.5" weight="bold" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CompletionCard({ eyebrow, homeCta, onNavigate }) {
  return (
    <div className="min-h-screen bg-[var(--ag-midnight)] text-[var(--ag-text)] flex items-center justify-center p-4 ag-radial">
      <div className="w-full max-w-md text-center space-y-4">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500/15 text-emerald-300 mx-auto">
          <CheckCircle size={40} weight="fill" />
        </div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ag-gold)] font-semibold">
          {eyebrow}
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-black tracking-tighter">
          ¡Todo listo!
        </h1>
        <p className="text-sm text-[var(--ag-text-dim)]">
          Has completado el proceso. Puedes empezar a usar AutoGlow.
        </p>
        <Button onClick={onNavigate} className="ag-btn-gold w-full h-11" data-testid="onboarding-completion-cta">
          <Sparkle size={14} weight="fill" className="mr-2" /> {homeCta}
        </Button>
      </div>
    </div>
  );
}

// Utility: para "resetear" onboarding desde consola (debug)
export function resetOnboarding(roleKey) {
  try { localStorage.removeItem(storageKey(roleKey)); } catch { /* noop */ }
}
